import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useMe } from "../../auth/AuthContext";
import { Notice } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { usePageTitle } from "../../lib/usePageTitle";
import { JobsTab } from "./JobsTab";
import { KommunerTab } from "./KommunerTab";
import { LeadsTab } from "./LeadsTab";
import { OrgsTab } from "./OrgsTab";
import { RunsTab } from "./RunsTab";

type Tab = "organisasjoner" | "kommuner" | "kjoringer" | "jobber" | "henvendelser";
const TABS: { id: Tab; label: MessageKey }[] = [
  { id: "organisasjoner", label: "admin.tabOrgs" },
  { id: "kommuner", label: "admin.tabKommuner" },
  { id: "kjoringer", label: "admin.tabRuns" },
  { id: "jobber", label: "admin.tabJobs" },
  { id: "henvendelser", label: "admin.tabLeads" },
];

/** Platform admin (US10): organisations, kommuner, analysis runs and jobs. */
export function AdminPage() {
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  usePageTitle(t("admin.title"));

  if (!me.user.is_platform_admin) {
    return (
      <div className="page narrow">
        <h1>{t("admin.title")}</h1>
        <Notice kind="error" role="alert">
          <p>{t("admin.noAccess")}</p>
        </Notice>
      </div>
    );
  }

  const active: Tab = TABS.find((x) => x.id === params.get("fane"))?.id ?? "organisasjoner";

  function select(tab: Tab, focus = false) {
    setParams(tab === "organisasjoner" ? {} : { fane: tab });
    if (focus) tabRefs.current[tab]?.focus();
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = TABS.findIndex((x) => x.id === active);
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (i + 1) % TABS.length;
    if (e.key === "ArrowLeft") next = (i - 1 + TABS.length) % TABS.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = TABS.length - 1;
    const tab = next === null ? undefined : TABS[next];
    if (tab) {
      e.preventDefault();
      select(tab.id, true);
    }
  }

  return (
    <div className="page page-wide">
      <header className="page-header">
        <h1>{t("admin.title")}</h1>
      </header>
      <div className="tabs" role="tablist" aria-label={t("admin.tabs")} onKeyDown={onKeyDown}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={active === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={active === tab.id ? 0 : -1}
            className={active === tab.id ? "tab active" : "tab"}
            onClick={() => select(tab.id)}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>
      <div
        className="tab-panel"
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
      >
        {active === "organisasjoner" && <OrgsTab />}
        {active === "kommuner" && <KommunerTab />}
        {active === "kjoringer" && <RunsTab />}
        {active === "jobber" && <JobsTab />}
        {active === "henvendelser" && <LeadsTab />}
      </div>
    </div>
  );
}
